import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../config/axios';
import { Meta, Display, Chip, Button, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState } from '../components/states';
import { useApi } from '../lib/api';
import { parseTaskInput } from '../lib/parseTaskInput';

/**
 * Add Task - dwupoziomowy (z 04-component-composition.md):
 *
 * Quick add (default): 1 input + auto-parse "jutro"/"podlej pomidory" + Dodaj
 * Full form (rozwinięte): typ pills + plant picker + when chips + recurring
 *
 * Endpointy:
 *   POST /api/tasks - tworzy zadanie
 *   /api/plots/all-with-details - lista bedów do plant matchera
 */

const TYPES = [
  { k: 'water',   label: 'Podlewanie', Icon: Icons.Drop,     color: 'var(--t-sage)' },
  { k: 'harvest', label: 'Zbiór',      Icon: Icons.Sprout,   color: 'var(--t-amber)' },
  { k: 'spray',   label: 'Oprysk',     Icon: Icons.Scissors, color: 'var(--t-rust)' },
  { k: 'custom',  label: 'Inne',       Icon: Icons.Sun,      color: 'var(--t-text-dim)' },
];

const WHEN = [
  { k: 'today',    label: 'Dziś' },
  { k: 'tomorrow', label: 'Jutro' },
  { k: 'weekend',  label: 'Weekend' },
  { k: 'custom',   label: 'Inna data' },
];

function isoDate(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function whenToDate(when) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (when === 'today') return isoDate(today);
  if (when === 'tomorrow') {
    const t = new Date(today); t.setDate(t.getDate() + 1);
    return isoDate(t);
  }
  if (when === 'weekend') {
    const t = new Date(today);
    const day = t.getDay();
    const diff = (6 - day + 7) % 7 || 7;
    t.setDate(t.getDate() + diff);
    return isoDate(t);
  }
  return null;
}

function flattenBeds(plots) {
  if (!Array.isArray(plots)) return [];
  return plots.flatMap(p =>
    (p.beds || []).map(b => ({ ...b, plot_name: p.name }))
  );
}

// ── Auto-parse preview ───────────────────────────────────────────
function ParsePreview({ parsed }) {
  if (!parsed.hasType && !parsed.hasDate && !parsed.hasPlant) {
    return (
      <div style={{
        marginTop: 12, fontSize: 12,
        color: 'var(--t-text-mute)', lineHeight: 1.5,
      }}>
        Wpisz np. „<span style={{ color: 'var(--t-text-dim)' }}>Podlej pomidory jutro</span>” —
        rozpoznajemy typ, datę i roślinę.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {parsed.hasType   && <Chip tone="sage">{parsed.typeLabel}</Chip>}
      {parsed.hasDate   && <Chip tone="amber">{parsed.dateLabel}</Chip>}
      {parsed.hasPlant  && <Chip tone="rust">{parsed.plantLabel}</Chip>}
    </div>
  );
}

// ── Type button (do full form) ───────────────────────────────────
function TypeBtn({ type, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        aspectRatio: '1/1', borderRadius: 12,
        background: selected ? 'var(--t-sage)' : 'var(--t-bg)',
        border: `1px solid ${selected ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, cursor: 'pointer',
        color: selected ? 'var(--t-bg)' : 'var(--t-text)',
        transition: 'transform 120ms ease, background 200ms ease',
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <type.Icon color={selected ? 'var(--t-bg)' : type.color} size={18} />
      <span style={{
        fontFamily: 'var(--t-mono)', fontSize: 8,
        letterSpacing: '.1em', textTransform: 'uppercase',
        textAlign: 'center', fontWeight: 500,
      }}>{type.label}</span>
    </button>
  );
}

// ── Plant picker ─────────────────────────────────────────────────
function PlantPicker({ beds, selectedBedId, onSelect }) {
  const selected = beds.find(b => b.id === selectedBedId);
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '14px 16px', borderRadius: 12,
          background: 'var(--t-bg)', border: '1px solid var(--t-line)',
          display: 'flex', alignItems: 'center', gap: 12,
          cursor: 'pointer',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, rgba(168,196,158,0.2), rgba(168,196,158,0.05))',
          display: 'grid', placeItems: 'center',
        }}>
          <Icons.Sprout color="var(--t-sage)" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <Display size={18} style={{ display: 'block', lineHeight: 1 }}>
                {selected.plant_name || 'Bez rośliny'}
              </Display>
              <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 2 }}>
                {[selected.plant_variety, selected.plot_name].filter(Boolean).join(' · ')}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: 'var(--t-text-dim)' }}>
              Wybierz grządkę (opcjonalne)
            </div>
          )}
        </div>
        <svg width="7" height="14" viewBox="0 0 7 14" fill="none"
             style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}>
          <path d="M1 1l5 6-5 6" stroke="var(--t-text-dim)" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {open && beds.length > 0 && (
        <div style={{
          marginTop: 6, maxHeight: 220, overflowY: 'auto',
          borderRadius: 10, background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
        }}>
          {selectedBedId && (
            <div
              onClick={() => { onSelect(null); setOpen(false); }}
              style={{
                padding: '10px 16px', fontSize: 13,
                color: 'var(--t-text-mute)', cursor: 'pointer',
                borderBottom: '1px solid var(--t-line)',
              }}
            >× Wyczyść</div>
          )}
          {beds.map(b => (
            <div
              key={b.id}
              onClick={() => { onSelect(b.id); setOpen(false); }}
              style={{
                padding: '10px 16px', fontSize: 13,
                color: 'var(--t-text)', cursor: 'pointer',
                borderBottom: '1px solid var(--t-line)',
                background: b.id === selectedBedId ? 'rgba(168, 196, 158, 0.08)' : 'transparent',
              }}
            >
              {b.plant_name || 'Pusty'} {b.plant_variety && `· ${b.plant_variety}`}
              <span style={{ color: 'var(--t-text-dim)', marginLeft: 6, fontSize: 11 }}>
                · {b.plot_name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Form body (wspólne) ──────────────────────────────────────────
function FormBody({ beds, parsed, override, setOverride, onSubmit, submitting, submitError, advanced, setAdvanced }) {
  const finalType = override.type ?? parsed.task_type;
  const finalBedId = override.bed_id ?? parsed.bed_id;
  const finalWhen = override.when ?? (parsed.due_date ? 'custom' : 'today');
  const finalRecurring = !!override.recurring;
  const customDate = override.customDate || parsed.due_date || isoDate(new Date());

  return (
    <>
      {/* Auto-parse preview */}
      <ParsePreview parsed={parsed.parsed} />

      {/* Toggle advanced mode */}
      <div
        onClick={() => setAdvanced(!advanced)}
        style={{
          marginTop: 18, marginBottom: advanced ? 22 : 18,
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.14em', textTransform: 'uppercase',
          color: 'var(--t-sage)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {advanced ? '↑ Mniej opcji' : '+ Więcej opcji'}
      </div>

      {advanced && (
        <>
          {/* Type */}
          <Meta style={{ marginBottom: 10 }}>Typ</Meta>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
            gap: 8, marginBottom: 22,
          }}>
            {TYPES.map(t => (
              <TypeBtn
                key={t.k}
                type={t}
                selected={finalType === t.k}
                onClick={() => setOverride({ ...override, type: t.k })}
              />
            ))}
          </div>

          {/* Plant picker */}
          <Meta style={{ marginBottom: 10 }}>Roślina</Meta>
          <div style={{ marginBottom: 22 }}>
            <PlantPicker
              beds={beds}
              selectedBedId={finalBedId}
              onSelect={(id) => setOverride({ ...override, bed_id: id })}
            />
          </div>

          {/* When */}
          <Meta style={{ marginBottom: 10 }}>Kiedy</Meta>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {WHEN.map(w => (
              <Chip
                key={w.k}
                selected={finalWhen === w.k}
                onClick={() => setOverride({ ...override, when: w.k })}
              >{w.label}{w.k === 'custom' ? ' ↗' : ''}</Chip>
            ))}
          </div>
          {finalWhen === 'custom' && (
            <input
              type="date"
              aria-label="Data zadania"
              value={customDate}
              onChange={e => setOverride({ ...override, customDate: e.target.value, when: 'custom' })}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10,
                background: 'var(--t-bg)', border: '1px solid var(--t-line)',
                color: 'var(--t-text)', fontFamily: 'var(--t-sans)', fontSize: 14,
                colorScheme: 'dark', marginBottom: 22,
              }}
            />
          )}

          {/* Recurring */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0',
            borderTop: '1px solid var(--t-line)',
            borderBottom: '1px solid var(--t-line)',
            marginBottom: 22,
          }}>
            <div>
              <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 2 }}>
                Powtarzaj co {override.recurringDays || 14} dni
              </div>
              <div style={{ fontSize: 11, color: 'var(--t-text-mute)' }}>
                Sukcesyjny wysiew, podlewanie itp.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOverride({ ...override, recurring: !finalRecurring })}
              aria-pressed={finalRecurring}
              style={{
                width: 44, height: 26, borderRadius: 13,
                background: finalRecurring ? 'var(--t-sage)' : 'var(--t-line-2)',
                position: 'relative', cursor: 'pointer',
                border: 'none', padding: 0,
                transition: 'background 200ms ease',
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: finalRecurring ? 20 : 2,
                width: 22, height: 22, borderRadius: '50%',
                background: 'var(--t-bg)',
                transition: 'left 200ms ease',
              }} />
            </button>
          </div>
        </>
      )}

      {submitError && (
        <div style={{
          padding: '12px 14px', borderRadius: 10,
          background: 'rgba(192, 106, 74, 0.08)',
          borderLeft: '3px solid var(--t-rust)',
          fontSize: 13, color: 'var(--t-text)',
          marginBottom: 14,
        }}>{submitError}</div>
      )}

      <Button
        full
        onClick={onSubmit}
        variant={parsed.description.length >= 3 && !submitting ? 'primary' : 'ghost'}
        icon={!submitting && <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M8 3v10M3 8h10" stroke="var(--t-bg)" strokeWidth="1.6" strokeLinecap="round" />
        </svg>}
      >{submitting ? 'Dodaję…' : 'Dodaj zadanie'}</Button>
    </>
  );
}

// ── Main page (z prawdziwym POST) ────────────────────────────────
export default function AddTask() {
  const { t } = useTranslation();
  const { isPhone } = useViewport();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [override, setOverride] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Wysokość klawiatury ekranowej (iOS/Android) — podnosimy arkusz, by przycisk
  // zapisu nie chował się za klawiaturą (visual viewport ≠ layout viewport na iOS).
  const [kbInset, setKbInset] = useState(0);

  useEffect(() => {
    if (!isPhone) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbInset(inset);
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [isPhone]);

  const { data: plotsData, loading: plotsLoading, error: plotsError } = useApi('/api/plots/all-with-details');

  const beds = useMemo(() => flattenBeds(plotsData), [plotsData]);
  const parsed = useMemo(() => parseTaskInput(input, beds), [input, beds]);

  if (plotsError && (plotsError.status === 401 || plotsError.message?.includes('autoryz'))) {
    return <Layout active="today"><ErrorState error={{ status: 401, message: plotsError.message }} /></Layout>;
  }

  const onSubmit = async () => {
    if (parsed.description.length < 3) {
      setSubmitError('Opisz zadanie krócej niż 3 znakami się nie da.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      task_type: override.type || parsed.task_type,
      description: parsed.description,
      due_date: override.when === 'custom'
        ? (override.customDate || parsed.due_date)
        : (whenToDate(override.when) || parsed.due_date),
      bed_id: override.bed_id !== undefined ? override.bed_id : parsed.bed_id,
      priority: 1,
    };

    if (override.recurring) {
      payload.is_recurring = true;
      payload.recurrence_frequency = override.recurringDays || 14;
    }

    try {
      await axios.post('/api/tasks', payload);
      navigate('/dashboard');
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Nie udało się zapisać. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (plotsLoading) return <Layout active="today"><LoadingState label={t('common.loading')} /></Layout>;

  // Mobile - bottom sheet
  if (isPhone) {
    return (
      <Layout active="today">
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 20, 16, 0.45)',
          zIndex: 50,
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-end',
          // podnosimy arkusz o wysokość klawiatury, by submit był widoczny/dosięgalny
          paddingBottom: kbInset,
          transition: 'padding-bottom 180ms ease',
        }}>
          <div style={{
            background: 'var(--t-bg-elev)',
            borderTopLeftRadius: 28, borderTopRightRadius: 28,
            padding: kbInset > 0
              ? '14px 24px 24px'
              : '14px 24px max(env(safe-area-inset-bottom), 40px)',
            border: '1px solid var(--t-line-2)', borderBottom: 'none',
            boxShadow: '0 -20px 60px rgba(0, 0, 0, 0.4)',
            maxHeight: '92dvh', overflow: 'auto',
          }}>
            <div style={{
              width: 40, height: 4, borderRadius: 2,
              background: 'var(--t-line-2)',
              margin: '0 auto 18px',
            }} />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 22,
            }}>
              <div>
                <Meta style={{ marginBottom: 4 }}>Nowe zadanie</Meta>
                <Display size={28} style={{
                  display: 'block', letterSpacing: '-.02em', lineHeight: 1,
                }}>Co robisz?</Display>
              </div>
              <button
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
                style={{
                  background: 'transparent', border: 'none',
                  fontFamily: 'var(--t-mono)', fontSize: 11,
                  letterSpacing: '.14em', textTransform: 'uppercase',
                  color: 'var(--t-sage)', cursor: 'pointer',
                }}
              >Zamknij</button>
            </div>

            <input
              type="text"
              aria-label="Treść zadania"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="np. Podlej pomidory jutro"
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 12,
                background: 'var(--t-bg)',
                border: '1px solid var(--t-line)',
                color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
                fontSize: 16, letterSpacing: '-.01em',
              }}
              onKeyDown={e => { if (e.key === 'Enter' && !advanced) onSubmit(); }}
            />

            <FormBody
              beds={beds} parsed={parsed} override={override} setOverride={setOverride}
              onSubmit={onSubmit} submitting={submitting} submitError={submitError}
              advanced={advanced} setAdvanced={setAdvanced}
            />
          </div>
        </div>
      </Layout>
    );
  }

  // Desktop - dedykowana strona
  return (
    <Layout active="today" topBar={<TopBar
      left={<Meta>Today · Nowe zadanie</Meta>}
      right={
        <span onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
              style={{ cursor: 'pointer', color: 'var(--t-sage)' }}>
          <Meta color="sage">← Wróć</Meta>
        </span>
      }
    />}>
      <div className="t-stagger" style={{
        padding: '40px 48px 60px',
        maxWidth: 720, margin: '0 auto',
      }}>
        <Meta style={{ marginBottom: 8 }}>Nowe zadanie</Meta>
        <Display size={'clamp(40px, 6vw, 64px)'} style={{
          display: 'block', lineHeight: 0.95, letterSpacing: '-.03em',
          marginBottom: 30, textWrap: 'balance',
        }}>
          Co robisz<span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}> dziś</span>?
        </Display>

        <input
          autoFocus
          type="text"
          aria-label="Treść zadania"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="np. Podlej pomidory jutro · Wysiej rzodkiewkę w sobotę · Przytnij bazylię"
          style={{
            width: '100%', padding: '18px 22px', borderRadius: 14,
            background: 'var(--t-bg)',
            border: '1px solid var(--t-line)',
            color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
            fontSize: 18, letterSpacing: '-.01em',
          }}
          onKeyDown={e => { if (e.key === 'Enter' && !advanced) onSubmit(); }}
        />

        <FormBody
          beds={beds} parsed={parsed} override={override} setOverride={setOverride}
          onSubmit={onSubmit} submitting={submitting} submitError={submitError}
          advanced={advanced} setAdvanced={setAdvanced}
        />
      </div>
    </Layout>
  );
}
